import { useRef, useState } from "react";
import { AUTH_URL, authFetch, getToken } from "../lib/api";
import { resizeImageToFile } from "../lib/ragConfig";

export const FacilitySettingsTab = ({ facility }) => {
  const fid = facility.facility_id;
  const [logoUrl, setLogoUrl] = useState(
    facility.logo_url || facility.config?.logo_url || "",
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileInputRef = useRef(null);

  const flash = (text, err = false) => {
    setMsg({ text, err });
    setTimeout(() => setMsg(null), 3500);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const resized = await resizeImageToFile(file, {
      maxWidth: 512,
      maxHeight: 512,
    });
    const formData = new FormData();
    formData.append("file", resized);
    formData.append("facility_id", fid);
    try {
      const token = getToken();
      const res = await fetch(`${AUTH_URL}/upload/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        flash(err.error || `Upload failed (${res.status})`, true);
        return;
      }
      const data = await res.json();
      if (data.url) {
        const full = `${AUTH_URL.replace("/auth", "")}${data.url}`;
        setLogoUrl(full);
        // Auto-save after upload
        await authFetch("/rag/ui-config", {
          method: "PUT",
          body: JSON.stringify({ facility_id: fid, logo_url: full }),
        });
        flash("Logo uploaded and saved");
      }
    } catch {
      flash("Upload failed", true);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authFetch("/rag/ui-config", {
        method: "PUT",
        body: JSON.stringify({ facility_id: fid, logo_url: logoUrl || "" }),
      });
      flash("Saved");
    } catch {
      flash("Save failed", true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-lg space-y-5">
      <h3 className="text-sm font-semibold text-slate-700">
        Facility Settings
      </h3>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-2">
          Logo
        </label>

        {/* Preview */}
        <div className="flex items-center gap-3 mb-3">
          <div className="h-14 w-14 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo preview"
                className="h-full w-full object-contain p-1"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <span className="text-slate-300 text-xs">None</span>
            )}
          </div>
          <div className="space-y-1.5">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="block px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? "Uploading…" : "Upload image"}
            </button>
            {logoUrl && (
              <button
                onClick={() => setLogoUrl("")}
                className="block text-xs text-slate-400 hover:text-red-500"
              >
                Remove
              </button>
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files?.[0])}
        />

        {/* URL fallback */}
        <input
          type="url"
          value={logoUrl}
          onChange={(e) => setLogoUrl(e.target.value)}
          placeholder="Or paste a URL: https://example.com/logo.png"
          className="w-full text-xs border border-slate-300 rounded px-3 py-2 font-mono text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-slate-400 mt-1">
          Replaces the Hyve logo in FieldIQ. Leave blank to use the default.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving || uploading}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {msg && (
          <span
            className={`text-xs font-medium ${msg.err ? "text-red-600" : "text-emerald-600"}`}
          >
            {msg.text}
          </span>
        )}
      </div>
    </div>
  );
};
